import { Component, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ProjectGroupComponent } from '../../list/project-group/project-group.component';
import { toProjectVms, sanitizeProjectDetails } from '../../list/list-grouping.util';
import { ProjectService } from '../../services/project.service';
import { AppService } from '../../services/app.service';

// CONNECT 2.0 (Spec 9, Task 3) — Project History restyled onto the Spec 4
// nested Project → Site → Product cards, in read-only mode: no bid/action
// controls, just the Completed status pill, facts, documents, due
// date/bid amount, and a View link into the record (`/project-history/:id`,
// Task 4's read-only workspace). Reuses the same fetch/group/render shape as
// Invitations/Pending/Active (see active-page.component.ts) — the only
// per-screen differences are the COMPLETED filter and `readOnly="true"`.
@Component({
  selector: 'project-history-page',
  standalone: true,
  imports: [CommonModule, ProjectGroupComponent],
  templateUrl: './project-history-page.component.html',
  styleUrl: './project-history-page.component.scss',
})
export class ProjectHistoryPageComponent implements OnDestroy {
  private readonly destroy$ = new Subject<void>();

  data: any = null;
  loading: boolean = true;

  constructor(
    private projectService: ProjectService,
    private appService: AppService
  ) {}

  ngOnInit() {
    this.fetchData();
    this.appService.setRedirectUrl('');
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  trackByProjectId(index: number, item: any): string {
    return item?.projectId ?? `${index}`;
  }

  fetchData() {
    this.projectService
      .fetchData()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        const filtered = sanitizeProjectDetails(result)
          .map((parent: any) => ({
            ...parent,
            // Mirrors the legacy flatMap's site check ("added the site check
            // for when user does a search") — only COMPLETED sites that still
            // carry a real `site` object.
            sites: parent.sites.filter(
              (child: any) => child?.proposal?.status === 'COMPLETED' && child?.site
            ),
          }))
          .filter((parent: any) => parent.sites.length > 0 && parent.projectId);
        // Group the flat project/site/proposal rows into the nested
        // Project → Site → Product view-model the CONNECT 2.0 cards render.
        this.data = toProjectVms(filtered);
        this.loading = false;
      });
  }
}
